import { Store } from 'vuex';

import { Criterion, EntityType } from '@/common/components/entity-form/EntityType';
import { isWeatherCriteria } from '@/common/enums/TargetingCriteriaType.enum';
import { Campaign, Creative, LineItem, Package, Plan, TargetingCriterion } from '@/common/models';
import { getStore, uniq } from '@/globals';

export default class EntityFormHelper {
  private readonly entity: EntityType;
  private readonly store: Store<any>;

  constructor(entity: EntityType) {
    this.store = getStore();
    this.entity = entity;
  }

  get isCampaign(): boolean {
    return this.entity instanceof Campaign;
  }

  get isCreative(): boolean {
    return this.entity instanceof Creative;
  }

  get isLineItem(): boolean {
    return this.entity instanceof LineItem;
  }

  get isPackage(): boolean {
    return this.entity instanceof Package;
  }

  get isPlan(): boolean {
    return this.entity instanceof Plan;
  }

  get isFromPackage(): boolean {
    return this.isLineItem && (this.entity as LineItem).isFromPackage;
  }

  get entityId(): string {
    switch (true) {
      case this.isCampaign:
        return 'campaign';
      case this.isCreative:
        return 'creative';
      case this.isLineItem:
        return 'lineitem';
      case this.isPackage:
        return 'package';
      case this.isPlan:
        return 'plan';
      default:
        return 'lineitem';
    }
  }

  getFormLabelId(prefix: string, suffix: string): string {
    return `${prefix}_${this.entityId}_${suffix}`;
  }

  updateEntityCriteria(value: Criterion[]): void {
    let action: string;
    let params: { plan?: Plan; lineItem?: LineItem; package?: Package; value: Criterion[] };

    if (this.isPackage) {
      action = 'updatePackageTargetingCriteria';
      params = {
        package: this.entity as Package,
        value,
      };
    }

    if (this.isLineItem) {
      action = 'updateLineItemTargetingCriteria';
      params = {
        lineItem: this.entity as LineItem,
        value,
      };
    }

    if (this.isPlan) {
      action = 'updatePlanTargetingCriteria';
      params = {
        plan: this.entity as Plan,
        value,
      };
    }

    if (!action && !params) {
      throw Error('Action and params needs to be defined');
    }

    this.updateEntityFeatureKeys(value);
    this.store.dispatch(action, params);
  }

  private updateEntityFeatureKeys(criteria: Criterion[]) {
    if (!this.isLineItem) return;

    const lineItem = this.entity as LineItem;
    const featureKeys = this.criterionFeatureKeys(criteria);
    const oldFeatureKeys = this.criterionFeatureKeys(lineItem.targetingCriteria);

    const newKeys = featureKeys.filter((k) => !oldFeatureKeys.includes(k) || !lineItem.hasFeature(k));
    const removedKeys = oldFeatureKeys.filter((k) => !featureKeys.includes(k) && lineItem.hasFeature(k));

    this.store.dispatch('updateLineItemAddFeatureKey', {
      lineItem: this.entity,
      value: newKeys,
    });
    this.store.dispatch('updateLineItemRemoveFeatureKey', {
      lineItem: this.entity,
      value: removedKeys,
    });
  }

  private criterionFeatureKeys(criteria: Criterion[]) {
    const targetingCriteria: TargetingCriterion[] = this.store.state.orgApp.ReferenceData.targetingCriteria.filter(
      (tc: TargetingCriterion) => {
        return criteria.some((c) => c.targetingCriterionID === tc.id);
      },
    );
    return uniq(
      targetingCriteria.map((criteria) => {
        if (criteria.isUpdatePushedExternally) return 'CUSTRIG_HS';
        else if (isWeatherCriteria(criteria.type)) return 'WEATHER_HS';
      }),
    ).filter((v) => !!v);
  }
}
